import { useCallback, useContext, useEffect, useRef, useState } from "react";
import { ZoomContext } from "../../zoom/zoom-context";
import { ZoomMediaContext } from "../../ZoomMediaProvider";
import { fetchSessionSignature } from "../utils/tokens";
import { RoomParams, SessionError, SessionPhase } from "../types";

interface SessionApi {
  phase: SessionPhase;
  error?: SessionError;
  participants: any[];
  selfId?: number;
  isHost: boolean;
  /** True only when the user is sending audio (started AND unmuted). */
  audioOn: boolean;
  audioMuted: boolean;
  audioStarted: boolean;
  /** True when the local camera is sending video. */
  videoOn: boolean;
  attempts: number;
  join: () => Promise<void>;
  leave: () => Promise<void>;
  rejoin: () => Promise<void>;
  toggleAudio: () => Promise<void>;
  toggleVideo: () => Promise<void>;
  setPhase: (p: SessionPhase) => void;
  /** Re-read getAllUser() into state. Useful when an external transition
   *  (e.g. guest being admitted from the lobby) needs to nudge the renderer
   *  to pick up streams that were already live before the UI mounted. */
  refresh: () => void;
}

const MAX_AUTO_REJOINS = 3;
const AUTO_REJOIN_DELAY_MS = 1500;
const GUEST_NAME_RE = /^Guest\s+(\d+)$/i;
const ANONYMOUS_GUEST_NAMES = new Set(["", "guest", "Guest"]);

/** True if this is the placeholder name carried by every public invite URL. */
function isAnonymousGuest(role: string, displayName: string): boolean {
  return role === "guest" && ANONYMOUS_GUEST_NAMES.has(displayName?.trim());
}

/** Compute the next free "Guest N" slot from the participant list. */
function nextGuestName(users: any[]): string {
  let highest = 0;
  for (const u of users) {
    const m = GUEST_NAME_RE.exec(u?.displayName ?? "");
    if (m) {
      const n = parseInt(m[1], 10);
      if (Number.isFinite(n) && n > highest) highest = n;
    }
  }
  return `Guest ${highest + 1}`;
}

export function useTelehealthSession(
  params: RoomParams,
  isPublicView: boolean
): SessionApi {
  const zmClient = useContext(ZoomContext);
  const mediaCtx = useContext(ZoomMediaContext);

  const [phase, setPhase] = useState<SessionPhase>("idle");
  const [error, setError] = useState<SessionError | undefined>();
  const [participants, setParticipants] = useState<any[]>([]);
  const [selfId, setSelfId] = useState<number | undefined>();
  const [audioStarted, setAudioStarted] = useState(false);
  const [audioMuted, setAudioMuted] = useState(true);
  const [videoOn, setVideoOn] = useState(false);
  const [attempts, setAttempts] = useState(0);

  // Mirrors `phase` for the connection-change listener, which is bound once
  // and would otherwise close over a stale value.
  const phaseRef = useRef<SessionPhase>(phase);
  useEffect(() => {
    phaseRef.current = phase;
  }, [phase]);

  const autoRejoinCountRef = useRef(0);
  const joinedRef = useRef(false);
  const pendingAutoRejoinRef = useRef<number | null>(null);
  const rejoinInFlightRef = useRef(false);
  // Phase to restore to when a transient Reconnecting → Connected blip
  // resolves. Without this, a guest who was waiting in the lobby when the
  // SDK hiccuped would resume in "active" and skip the admit handshake —
  // which manifests as a patient sitting in the empty meeting view while
  // the host never sees a knock.
  const phaseBeforeReconnectRef = useRef<SessionPhase | null>(null);

  const isHost = params.role === "host";

  const refreshParticipants = useCallback(() => {
    if (!zmClient) return;
    try {
      setParticipants(zmClient.getAllUser());
      // Catch-up on our own userId if it wasn't populated when join()
      // resolved. Without this, selfId can stay undefined indefinitely —
      // which silently breaks two things: the host's self-tile fallback
      // (no participant tile, "Waiting for others to join…" forever) and
      // the guest's knock loop (the lobby effect bails on !selfId, so
      // the host never sees an admit prompt).
      setSelfId((cur) => {
        if (cur !== undefined) return cur;
        try {
          return zmClient.getCurrentUserInfo()?.userId;
        } catch {
          return cur;
        }
      });
    } catch {
      /* not yet in session */
    }
  }, [zmClient]);

  // ----- core join/leave --------------------------------------------------
  const doJoin = useCallback(async () => {
    if (!zmClient || !mediaCtx) {
      setError({
        code: "NO_CLIENT",
        message: "Zoom client not initialized",
        retryable: true,
      });
      return;
    }
    if (joinedRef.current) return;

    setPhase("joining");
    setError(undefined);
    setAttempts((a) => a + 1);

    try {
      const signature = await fetchSessionSignature(params.room, isPublicView);
      if (!signature) throw new Error("Empty signature");

      await zmClient.join(params.room, signature, params.displayName);
      joinedRef.current = true;
      autoRejoinCountRef.current = 0;

      // Anonymous guests get auto-numbered as "Guest N". Done after join so
      // we can see the existing names and avoid colliding with other guests.
      if (isAnonymousGuest(params.role, params.displayName)) {
        try {
          const users = zmClient.getAllUser?.() ?? [];
          const newName = nextGuestName(users);
          await (zmClient as any).changeName?.(newName);
        } catch (e) {
          console.warn("guest auto-rename failed", e);
        }
      }

      const stream: any = zmClient.getMediaStream();
      mediaCtx.setMediaStream(stream);

      // Start audio (mic permission prompt fires here on first join).
      try {
        await stream.startAudio();
        setAudioStarted(true);
        try {
          await stream.muteAudio();
          setAudioMuted(true);
        } catch {
          setAudioMuted(false);
        }
      } catch (e) {
        console.warn("startAudio failed", e);
      }

      const me = zmClient.getCurrentUserInfo();
      setSelfId(me?.userId);

      // The local user isn't always in getAllUser() the instant join()
      // resolves — the SDK populates the roster asynchronously. Schedule a
      // few follow-up refreshes so the host's own tile shows up on first
      // join (otherwise it only appeared after a manual rejoin).
      refreshParticipants();
      window.setTimeout(refreshParticipants, 250);
      window.setTimeout(refreshParticipants, 1000);
      window.setTimeout(refreshParticipants, 2500);

      setPhase(isHost ? "active" : "lobby");
    } catch (err: any) {
      joinedRef.current = false;
      setError({
        code: err?.type ?? "JOIN_FAILED",
        message: err?.reason ?? err?.message ?? "Could not connect",
        retryable: true,
      });
      setPhase("disconnected");
    }
  }, [zmClient, mediaCtx, params, isPublicView, isHost, refreshParticipants]);

  const doLeave = useCallback(async () => {
    if (!zmClient) return;
    try {
      const stream: any = zmClient.getMediaStream?.();
      if (stream) {
        try {
          if (videoOn) await stream.stopVideo();
        } catch {
          /* ignore */
        }
        try {
          if (audioStarted) await stream.stopAudio?.();
        } catch {
          /* ignore */
        }
      }
      await zmClient.leave();
    } catch {
      /* ignore */
    }
    joinedRef.current = false;
    phaseBeforeReconnectRef.current = null;
    setVideoOn(false);
    setAudioStarted(false);
    setAudioMuted(true);
    setPhase("ended");
  }, [zmClient, videoOn, audioStarted]);

  const doRejoin = useCallback(async () => {
    if (rejoinInFlightRef.current) return;
    rejoinInFlightRef.current = true;

    // Immediate UI feedback: flip to "joining" right away so RejoinPrompt
    // unmounts and the preflight "Joining…" spinner takes over. Without
    // this, the popup stays visible for ~400ms+ of cleanup and the click
    // feels like it did nothing.
    setPhase("joining");

    // Cancel any auto-rejoin the connection-change handler had queued —
    // otherwise it will collide with this manual rejoin and one of the two
    // join() calls will hang or fail with "already in session".
    if (pendingAutoRejoinRef.current !== null) {
      window.clearTimeout(pendingAutoRejoinRef.current);
      pendingAutoRejoinRef.current = null;
    }
    autoRejoinCountRef.current = 0;
    phaseBeforeReconnectRef.current = null;

    try {
      // Always call leave(), even when our local flag says we're not in a
      // session. After a Closed/Fail event the flag is false but Zoom's
      // internal state can still have residue that makes the next join()
      // reject with "already in session". leave() is a no-op when there
      // really is nothing to clean up.
      try {
        await zmClient?.leave();
      } catch {
        /* ignore */
      }
      joinedRef.current = false;

      // Reset stale state from the prior connection so the UI doesn't show
      // ghost participant tiles or the wrong mic icon while we're joining.
      setParticipants([]);
      setSelfId(undefined);
      setVideoOn(false);
      setAudioStarted(false);
      setAudioMuted(true);
      setError(undefined);

      // Brief settle window — the SDK's leave() finishes async cleanup
      // shortly after the promise resolves; a join() that fires inside
      // that window can be rejected with "already in session".
      await new Promise((r) => setTimeout(r, 400));

      await doJoin();
    } finally {
      rejoinInFlightRef.current = false;
    }
  }, [zmClient, doJoin]);

  // ----- best-effort cleanup on tab close ---------------------------------
  // The Zoom SDK takes tens of seconds to mark a silently-disconnected user
  // as removed. Calling leave() during pagehide gives the server a chance
  // to fire `user-removed` to other peers (notably the host's lobby channel)
  // immediately, so a guest who closes their tab while waiting in the lobby
  // disappears from the host's knock list right away.
  useEffect(() => {
    if (!zmClient) return;
    const onPageHide = () => {
      try {
        zmClient.leave();
      } catch {
        /* tab is going away — nothing to recover */
      }
    };
    window.addEventListener("pagehide", onPageHide);
    window.addEventListener("beforeunload", onPageHide);
    return () => {
      window.removeEventListener("pagehide", onPageHide);
      window.removeEventListener("beforeunload", onPageHide);
    };
  }, [zmClient]);

  // ----- auto-reconnect on connection-change ------------------------------
  useEffect(() => {
    if (!zmClient) return;
    const onConnectionChange = (payload: any) => {
      const state = payload?.state;
      if (state === "Reconnecting") {
        setPhase((cur) => {
          // Snapshot the prior phase so we can restore it once we're back.
          // Don't overwrite the snapshot on a repeat Reconnecting event —
          // we want the original pre-blip phase, not "reconnecting".
          if (cur !== "reconnecting") {
            phaseBeforeReconnectRef.current = cur;
          }
          return "reconnecting";
        });
      } else if (state === "Connected") {
        setPhase((cur) => {
          if (cur !== "reconnecting") return cur;
          const prior = phaseBeforeReconnectRef.current;
          phaseBeforeReconnectRef.current = null;
          // A guest mid-knock should resume knocking, not slip into the
          // meeting unadmitted; a host who lost connection during a live
          // call should land back in "active". When we have no snapshot
          // (first connect after join), fall back to the role default.
          if (prior === "lobby" || prior === "active") return prior;
          return isHost ? "active" : "lobby";
        });
      } else if (state === "Closed" || state === "Fail") {
        joinedRef.current = false;
        // A denial removes us from the session, so the Closed event that
        // follows is expected. Auto-rejoining here would put the guest
        // straight back in the lobby against the host's decision — they
        // re-enter only by clicking "Ask to join again".
        if (phaseRef.current === "denied") return;
        setPhase("disconnected");
        if (autoRejoinCountRef.current < MAX_AUTO_REJOINS) {
          autoRejoinCountRef.current += 1;
          pendingAutoRejoinRef.current = window.setTimeout(() => {
            pendingAutoRejoinRef.current = null;
            // doRejoin is the safer path: it leaves cleanly and waits a
            // settle window, which is what the SDK needs after a Fail.
            doRejoin();
          }, AUTO_REJOIN_DELAY_MS * autoRejoinCountRef.current);
        }
      }
    };
    zmClient.on("connection-change", onConnectionChange);
    return () => {
      zmClient.off("connection-change", onConnectionChange);
    };
  }, [zmClient, doRejoin]);

  // ----- participant + media state tracking ------------------------------
  useEffect(() => {
    if (!zmClient) return;
    const handler = () => refreshParticipants();
    zmClient.on("user-added", handler);
    zmClient.on("user-removed", handler);
    zmClient.on("user-updated", handler);
    zmClient.on("peer-video-state-change", handler);
    return () => {
      zmClient.off("user-added", handler);
      zmClient.off("user-removed", handler);
      zmClient.off("user-updated", handler);
      zmClient.off("peer-video-state-change", handler);
    };
  }, [zmClient, refreshParticipants]);

  // Keep audioMuted in sync with the SDK whenever we're muted/unmuted by
  // any path (host force-mute, browser audio policy, our own toggle). If
  // the SDK auto-starts audio (e.g. on a join with default-on), reflect
  // that into audioStarted too.
  useEffect(() => {
    if (!zmClient) return;
    const onAudioChange = (payload: any) => {
      if (payload?.action === "muted") {
        setAudioMuted(true);
        setAudioStarted(true);
      } else if (payload?.action === "unmuted") {
        setAudioMuted(false);
        setAudioStarted(true);
      } else if (payload?.action === "join") {
        setAudioStarted(true);
      } else if (payload?.action === "leave") {
        setAudioStarted(false);
        setAudioMuted(true);
      }
    };
    zmClient.on("current-audio-change", onAudioChange);
    return () => zmClient.off("current-audio-change", onAudioChange);
  }, [zmClient]);

  // ----- audio: toggle mute on existing stream ---------------------------
  const toggleAudio = useCallback(async () => {
    const stream: any = zmClient?.getMediaStream();
    if (!stream) return;
    try {
      if (!audioStarted) {
        // Edge case: initial startAudio failed; try again here (this call
        // happens inside a click handler so the gesture is fresh).
        await stream.startAudio();
        setAudioStarted(true);
        setAudioMuted(false);
        return;
      }
      if (audioMuted) {
        await stream.unmuteAudio();
        setAudioMuted(false);
      } else {
        await stream.muteAudio();
        setAudioMuted(true);
      }
    } catch (e) {
      console.warn("toggleAudio failed", e);
    }
  }, [zmClient, audioStarted, audioMuted]);

  // ----- video: start/stop camera ----------------------------------------
  const toggleVideo = useCallback(async () => {
    const stream: any = zmClient?.getMediaStream();
    if (!stream) return;
    try {
      if (videoOn) {
        await stream.stopVideo();
        setVideoOn(false);
      } else {
        await stream.startVideo();
        setVideoOn(true);
      }
      // The local user's bVideoOn flips synchronously after start/stop, but
      // the SDK doesn't always fire a participant-list event for our own
      // change — refresh manually so the local tile mounts/unmounts.
      refreshParticipants();
    } catch (e) {
      console.warn("toggleVideo failed", e);
    }
  }, [zmClient, videoOn, refreshParticipants]);

  return {
    phase,
    error,
    participants,
    selfId,
    isHost,
    audioOn: audioStarted && !audioMuted,
    audioMuted,
    audioStarted,
    videoOn,
    attempts,
    join: doJoin,
    leave: doLeave,
    rejoin: doRejoin,
    toggleAudio,
    toggleVideo,
    setPhase,
    refresh: refreshParticipants,
  };
}
